import { inject, Injectable } from '@angular/core';
import { AuthWorkflowState } from './auth-workflow-state.enum';
import { Router } from '@angular/router';
import { LocalStorageService } from '../storage/local-storage.service';
import { AuthFactoryService } from './auth-factory.service';

@Injectable({
  providedIn: 'root',
})
export class AuthOrchestratorService {
  private readonly authFactory = inject(AuthFactoryService);
  private readonly localStorageService = inject(LocalStorageService);

  private readonly router = inject(Router);

  async handleCallback(callbackState: string): Promise<void> {
    const provider = this.authFactory.getProviderByCallbackState(callbackState);

    if (!provider) {
      await this.router.navigate(['/home']);
      return;
    }

    try {
      await provider.parseFromUrl();
      this.processWorkflow();
    } catch (err: any) {
      if (err.errorCode === 'access_denied') {
        await this.router.navigate(['/access-error'], {
          queryParams: { message: 'callback-error' },
        });
        return;
      }
      throw err;
    }
  }

  async processWorkflow(): Promise<void> {
    const currentState = this.getWorkflowState();

    let isAuthenticated = false;

    switch (currentState) {
      case AuthWorkflowState.DefaultAuth:
      case AuthWorkflowState.PrincipalAuth:
        isAuthenticated = await this.authFactory.getProvider('principal').signIn();
        break;

      case AuthWorkflowState.OasAuth:
        isAuthenticated = await this.authFactory.getProvider('oas').signIn();
        break;

      case AuthWorkflowState.MsAuth:
        isAuthenticated = await this.authFactory.getProvider('ms').signIn();
        break;

      case AuthWorkflowState.FullyAuthenticated:
        await this.router.navigate(['/home']);
        break;

      default:
        throw new Error(`Unknown workflow state ${currentState}`);
    }

    if (isAuthenticated) {
      this.moveToNextWorkflowState();
    }
  }

  restartWorkflow(): Promise<void> {
    this.setWorkflowState(AuthWorkflowState.DefaultAuth);
    return this.processWorkflow();
  }

  private async moveToNextWorkflowState(): Promise<void> {
    const currentState = this.getWorkflowState();
    let nextState = AuthWorkflowState.DefaultAuth;

    switch (currentState) {
      case AuthWorkflowState.DefaultAuth:
        nextState = AuthWorkflowState.PrincipalAuth;
        break;
      case AuthWorkflowState.PrincipalAuth:
        nextState = AuthWorkflowState.OasAuth;
        break;
      case AuthWorkflowState.OasAuth:
        nextState = AuthWorkflowState.MsAuth;
        break;
      case AuthWorkflowState.MsAuth:
        nextState = AuthWorkflowState.FullyAuthenticated;
        break;
      case AuthWorkflowState.FullyAuthenticated:
        break;
      default:
        console.error(`Unknown state ${currentState}`);
        await this.restartWorkflow();
        return;
    }

    this.setWorkflowState(nextState);
    this.processWorkflow();
  }

  getWorkflowState(): AuthWorkflowState {
    return (
      (this.localStorageService.get('auth-workflow-state') as AuthWorkflowState) ??
      AuthWorkflowState.DefaultAuth
    );
  }

  private setWorkflowState(workflowState: AuthWorkflowState): void {
    this.localStorageService.set('auth-workflow-state', workflowState);
  }
}
